import { type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { EntitySectionLayout } from '@/components/composed/EntitySectionLayout';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { MaskedCell } from '@/components/composed/MaskedCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { EmptyState } from '@/components/composed/EmptyState';
import { resolveSection } from '@/lib/entity-sections';
import { tpaDetailQuery } from '../api/tpa-queries';
import type { TpaId } from '@/lib/api/ids';
import { tpaSections } from '../tpa-sections';

/*
 * TpaDetail — clone of ProviderDetail/GeneralAgentDetail, a consumer of the
 * composed EntitySectionLayout.
 *
 * The route resolves the active section from the `$section` param and PUBLISHES
 * the entity sub-nav (sections / active id / entity name / router `<Link>`) into
 * the shell rail (via `useRailSubNav`). This component owns only the
 * domain wiring left in the content column: resolving which body to render for
 * the active section. It is navigation-mechanism-agnostic — it never imports a
 * router.
 */
export function TpaDetail({
  tpaId,
  section,
}: {
  tpaId: TpaId;
  /** Raw `$section` route param (may be missing/invalid). */
  section: string | undefined;
}) {
  const { data: tpa } = useSuspenseQuery(tpaDetailQuery(tpaId));
  const activeSectionId =
    resolveSection(section, tpaSections) ?? tpaSections[0]!.id;

  return (
    <EntitySectionLayout>
      <SectionBody sectionId={activeSectionId} tpa={tpa} />
    </EntitySectionLayout>
  );
}

function SectionBody({
  sectionId,
  tpa,
}: {
  sectionId: string;
  tpa: import('@/lib/api/domains/tpas').Tpa;
}) {
  if (sectionId === 'profile') {
    return (
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Name">
          <span className="font-medium">{tpa.name}</span>
        </Field>
        {tpa.publicId ? (
          <Field label="Public ID">
            <IdCell value={tpa.publicId} />
          </Field>
        ) : null}
        {tpa.taxId ? (
          <Field label="TIN">
            <MaskedCell value={tpa.taxId} />
          </Field>
        ) : null}
        <Field label="Status">
          <StatusBadge
            label={tpa.status === 'active' ? 'Active' : 'Inactive'}
            tone={tpa.status === 'active' ? 'success' : 'neutral'}
          />
        </Field>
        <Field label="Created">
          <DateCell value={tpa.createdAt} />
        </Field>
      </dl>
    );
  }

  // The remaining sections (commissions, contracts, bank-accounts,
  // disbursement-schedules, distribution-rules) have no FE contract yet — their
  // endpoints are the entity sub-domains that land in later slices. The layout
  // + deep-link routing is fully wired now; each section fills in when its data
  // contract exists.
  const label = tpaSectionLabel(sectionId) ?? sectionId.replace(/-/g, ' ');
  return (
    <EmptyState
      title={label}
      description="This section will be available in a later release."
    />
  );
}

function tpaSectionLabel(id: string): string | undefined {
  return tpaSections.find((s) => s.id === id)?.label;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
